import { FaHeart } from "react-icons/fa";
import { FaRegHeart } from "react-icons/fa";
import { Card } from "./Card";
import { IoChatbubbleEllipsesOutline } from "react-icons/io5";
import Avatar from "./Avatar";
import OptionsSelector from "./OptionsSelector";
import { useRef, useState } from "react";
import clsx from "clsx";
import { ImAttachment } from "react-icons/im";
import { FiImage } from "react-icons/fi";
import { UserContext } from "../../../../App";
import { useContext } from "react";
import FeedApi from "../../../../API/FeedApi";
import { useEffect } from "react";
import { format } from "timeago.js";
import { BsExclamationCircleFill } from "react-icons/bs";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import SocialFeedSkeleton, {
	SocialFeedListSkeleton,
} from "./skeletons/SocialFeedSkeleton";
import { Pagination, PaginationItem } from "@mui/material";
import { BiLeftArrow, BiRightArrow } from "react-icons/bi";
import { X } from "lucide-react";

const SocialFeedItem = ({
	username,
	timestamp,
	content,
	profilePic,
	likes,
	comments,
	images,
	isOwner,
	isImportant,
	onCommentClick,
	id,
	isLikedByUser,
	userId,
	updatePosts,
}) => {
	return (
		<div
			className={clsx(
				"bg-white pt-4 rounded-lg shadow-sm border overflow-hidden shrink-0",
				isImportant ? "border-blue-300" : "border-gray-200",
			)}
		>
			<div className="flex items-center space-x-3 mb-3 px-4">
				<Avatar src={profilePic} name={username} />
				<div>
					<div className="gap-2 flex items-center">
						<p className="font-semibold text-sm">
							{isOwner ? "You" : username}
						</p>
						<div className="bg-gray-500 p-0.5 rounded-full"></div>
						<p className="text-xs text-gray-500">
							{format(new Date(timestamp), "en_US")}
						</p>
					</div>
					{isImportant && (
						<p className="text-xs font-bold text-blue-500 flex gap-1 items-center">
							<BsExclamationCircleFill /> Important
						</p>
					)}
				</div>
			</div>

			<p className="text-sm text-gray-700 mb-3 px-4">{content}</p>

			{images && (
				<div className="flex overflow-x-auto space-x-4 snap-x snap-mandatory pt-4 pb-2 px-4">
					{images.map((url, i) => (
						<div
							key={i}
							className="snap-center flex-shrink-0 w-80 max-w-[65vw] h-80 first:ml-2 last:mr-2"
						>
							<img
								src={url}
								alt={`Post ${i + 1}`}
								className="w-full h-full object-cover rounded-lg"
							/>
						</div>
					))}
				</div>
			)}

			<div className="flex items-center justify-start text-gray-500 gap-6 px-4 py-4 bg-gray-50">
				{isLikedByUser ? (
					<div
						className="flex items-center space-x-2 cursor-pointer text-red-500 hover:scale-105"
						onClick={() => {
							FeedApi.unlikePost({ id, userId }).then((res) => {
								if (res.status.success) {
									updatePosts((ps) => {
										console.log("I am fucking here");
										return ps.map((p) =>
											p.id === id
												? { ...p, likesCount: res.data, isLikedByUser: false }
												: p,
										);
									});
								}
							});
						}}
					>
						<FaHeart className="w-5 h-5" />
						<span className="text-xs">{likes}</span>
					</div>
				) : (
					<div
						className="flex items-center space-x-2 cursor-pointer hover:text-red-500 hover:scale-105"
						onClick={() => {
							FeedApi.likePost({ id, userId }).then((res) => {
								if (res.status.success) {
									updatePosts((ps) => {
										console.log("I am fucking here");
										return ps.map((p) =>
											p.id === id
												? { ...p, likesCount: res.data, isLikedByUser: true }
												: p,
										);
									});
								}
							});
						}}
					>
						<FaRegHeart className="w-5 h-5 fill-current" />
						<span className="text-xs">{likes}</span>
					</div>
				)}
				<div
					className="flex items-center space-x-2 cursor-pointer hover:text-blue-500"
					onClick={() => onCommentClick(id)}
				>
					<IoChatbubbleEllipsesOutline className="w-5 h-5" />
					<span className="text-xs">{comments}</span>
				</div>
			</div>
		</div>
	);
};

const SocialPostForm = ({ id, updatePosts, firebaseId }) => {
	const [isImportant, setIsImportant] = useState(false);
	const [content, setContent] = useState("");
	const [attachedFile, setAttachedFile] = useState(null);
	const [uploadedImages, setUploadedImages] = useState([]);
	const [submitting, setSubmitting] = useState(false);

	const fileInputRef = useRef(null);
	const imageInputRef = useRef(null);

	const handleFileChange = (e) => {
		if (e.target.files && e.target.files[0]) {
			const file = e.target.files[0];
			setAttachedFile(file);
		}
	};

	const handleImageChange = (e) => {
		if (e.target.files && e.target.files.length > 0) {
			const newImages = Array.from(e.target.files);
			setUploadedImages((prev) => [...prev, ...newImages]);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setSubmitting(true);

		const imageUrls = [];

		try {
			const storage = getStorage();
			for (let i = 0; i < uploadedImages.length; i++) {
				const image = uploadedImages[i];
				const uniqueId = uuidv4();
				const storageRef = ref(
					storage,
					`social_posts/${firebaseId}/${uniqueId}`,
				);
				await uploadBytes(storageRef, image);
				const url = await getDownloadURL(storageRef);
				imageUrls.push(url);
			}
		} catch (error) {
			setSubmitting(false);
			return console.error("Upload failed:", error);
		}

		const res = await FeedApi.createPost({
			content: content.trim(),
			userId: id,
			isImportant,
			imageUrls,
		});
		if (res.status.success) {
			setIsImportant(false);
			setContent("");
			setUploadedImages([]);
			setAttachedFile(null);
			updatePosts(res.data);
		}
		setSubmitting(false);
	};

	const removeImage = (index) => {
		setUploadedImages((prev) => prev.filter((_, i) => i !== index));
	};

	const removeAttachedFile = () => {
		setAttachedFile(null);
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	return (
		<form onSubmit={handleSubmit} className={submitting ? "opacity-80" : ""}>
			<div className="mb-6 px-4 bg-white border-b border-gray-200">
				<textarea
					type="text"
					placeholder="What's Happening?"
					className="w-full p-3 text-sm rounded-t-lg rounded-md border border-gray-200 focus:outline-none resize-none"
					rows={3}
					onChange={(e) => setContent(e.target.value)}
					value={content}
				></textarea>
				{uploadedImages.length > 0 && (
					<div className="mt-2 flex flex-wrap gap-2">
						{uploadedImages.map((img, index) => (
							<div
								key={index}
								className="relative w-16 h-16 bg-gray-100 rounded overflow-hidden"
							>
								<img
									src={URL.createObjectURL(img)}
									alt={`Preview ${index}`}
									className="w-full h-full object-cover"
								/>
								<button
									onClick={() => removeImage(index)}
									className="absolute top-0 right-0 bg-red-500 text-white text-xs w-4 h-4 flex items-center justify-center rounded-full"
								>
									×
								</button>
							</div>
						))}
					</div>
				)}
				{attachedFile && (
					<div className="mt-2 flex items-center p-2 bg-gray-50 rounded">
						<span className="text-xs truncate flex-1">{attachedFile.name}</span>
						<button
							onClick={removeAttachedFile}
							className="ml-2 text-red-500 text-xs hover:text-red-700"
						>
							Remove
						</button>
					</div>
				)}
				<div className="flex justify-between items-center p-3">
					<div className="flex gap-1 flex-wrap lg:flex-nowrap">
						<div className="grow lg:grow-0">
							<label className="text-sm p-1 rounded relative flex items-center gap-1 cursor-pointer">
								<input
									type="checkbox"
									checked={isImportant}
									onChange={(e) => setIsImportant(e.target.checked)}
									className="absolute w-0 h-0 opacity-0"
								/>
								<div
									className={clsx(
										"p-1 rounded-full border-solid border transition-colors",
										isImportant
											? "bg-blue-500 border-blue-500"
											: "bg-white border-gray-200",
									)}
								>
									<div className="p-0.5 bg-white rounded-full"></div>
								</div>
								<div
									className={clsx(
										"transition-colors",
										isImportant ? "text-gray-800" : "text-gray-500",
									)}
								>
									Mark as Important
								</div>
							</label>
						</div>
						<label className="text-xs text-gray-500 hover:bg-gray-100 p-1 rounded flex items-center gap-1">
							<FiImage />
							<div>Media</div>
							<input
								type="file"
								ref={imageInputRef}
								onChange={handleImageChange}
								className="hidden"
								accept="image/*"
								multiple
							/>
						</label>
					</div>
					<button
						disabled={submitting}
						className="disabled:bg-gray-600 bg-blue-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-blue-700"
					>
						Post
					</button>
				</div>
			</div>
		</form>
	);
};

const CommentModal = ({ onClose, post, userId, updatePosts }) => {
	const [loading, setLoading] = useState(true);
	const [comments, setComments] = useState();
	const [content, setContent] = useState("");

	useEffect(() => {
		FeedApi.getAllComments({ postId: post.id }).then((res) => {
			if (res.status.success) {
				setComments(res.data);
				setLoading(false);
			}
		});
	}, [post]);

	const handleFormSubmit = (e) => {
		e.preventDefault();
		if (!content) return;
		FeedApi.createPostComment({ postId: post.id, content, userId }).then(
			(res) => {
				if (res.status.success) {
					setComments([...comments, res.data]);
					setContent("");
					updatePosts((ps) => {
						return ps.map((p) =>
							p.id === post.id
								? { ...p, commentsCount: p.commentsCount + 1 }
								: p,
						);
					});
				}
			},
		);
	};

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
			<div className="bg-white rounded-lg w-full max-w-xl max-h-[80vh] flex flex-col">
				<div className="p-4 border-b flex justify-between items-center">
					<h3 className="font-bold">Post</h3>
					<button
						onClick={onClose}
						className="text-gray-500 hover:text-gray-700"
					>
						<X size={20} />
					</button>
				</div>

				<div className="p-4 border-b overflow-y-auto overflow-x-hidden">
					<div className="flex gap-3 mb-4 overflow-x-hidden">
						<Avatar src={post.profilePic} name={post.username} />
						<div className="flex-1 overflow-x-hidden">
							<div className="flex items-center gap-2">
								<span className="font-medium">{post.username}</span>
								<span className="text-xs text-muted-foreground">
									{format(new Date(post.timestamp), "en_US")}
								</span>
							</div>
							<p className="text-sm text-gray-700 mb-3">{post.content}</p>

							{post.images && post.images.length > 0 && (
								<div className="flex overflow-x-auto space-x-4 snap-x snap-mandatory">
									{post.images.map((url, i) => (
										<div
											key={i}
											className="snap-center flex-shrink-0 w-60 h-60 first:ml-2 last:mr-2"
										>
											<img
												src={url}
												alt={`Post ${i + 1}`}
												className="w-full h-full object-cover rounded-lg"
											/>
										</div>
									))}
								</div>
							)}
						</div>
					</div>
					<hr className="my-4" />

					<div className="space-y-4">
						<h4 className="font-semibold text-sm">Comments</h4>
						{!loading &&
							comments.map((comment) => (
								<div key={comment.id} className="flex gap-3">
									<Avatar src={comment.profilePic} name={comment.userName} />
									<div>
										<div className="flex items-center gap-2">
											<span className="font-medium text-sm">
												{comment.userName}
											</span>
											<span className="text-xs text-gray-500">
												{format(new Date(comment.timestamp), "en_US")}
											</span>
										</div>
										<p className="text-sm text-gray-700">{comment.content}</p>
									</div>
								</div>
							))}
					</div>
				</div>

				<div className="p-4">
					<form onSubmit={handleFormSubmit}>
						<textarea
							placeholder="Add a comment..."
							className="w-full p-3 text-sm rounded-lg border border-gray-200 focus:outline-none resize-none"
							rows={2}
							value={content}
							onChange={(e) => setContent(e.target.value)}
						></textarea>
						<div className="flex justify-end mt-2">
							<button
								type="submit"
								className="bg-blue-600 text-white text-sm px-4 py-2 rounded-lg hover:bg-blue-700"
							>
								Comment
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	);
};

const SocialFeed = () => {
	const [activeTab, setActiveTab] = useState("ALL");
	const [posts, setPosts] = useState([]);
	const [loading, setLoading] = useState(true);
	const [init, setInit] = useState(true);
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(0);
	const [selectedPost, setSelectedPost] = useState(null);
	const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);

	const { id, firstName, lastName, firebaseId, userType } =
		useContext(UserContext);

	useEffect(() => {
		setLoading(true);
		FeedApi.getAllPosts({
			userId: id,
			isImportant: activeTab === "IMPORTANT",
			pagingParams: {
				pageNumber: page,
				pageSize: 10,
			},
		})
			.then((res) => {
				setPosts(res.data.list);
				setTotalPages(res.data.totalPages || 1);
				setLoading(false);
				setInit(false);
			})
			.catch((err) => {
				console.error(err);
				setLoading(false);
				setInit(false);
			});
	}, [activeTab, page, id]);

	if (init) return <SocialFeedSkeleton />;

	const updatePosts = (post) =>
		setPosts((ps) => [
			{
				...post,
				userName: `${firstName} ${lastName}`,
			},
			...ps,
		]);

	const handleCommentClick = (postId) => {
		const post = posts.find((p) => p.id === postId);
		if (post) {
			setSelectedPost(post);
			setIsCommentModalOpen(true);
		}
	};
	return (
		<Card className="max-h-[800px] flex flex-col overflow-y-auto relative">
			<div className="mb-2 p-3 lg:p-6 py-5 px-6 flex justify-between items-center bg-white border-b border-b-neutral-200 sticky top-0 z-10">
				<h3 className="font-bold">Feed</h3>
				<OptionsSelector
					className="px-4"
					options={[
						{ label: "All", value: "ALL" },
						{ label: "Important", value: "IMPORTANT" },
					]}
					onSelect={setActiveTab}
					selected={activeTab}
					detached
				/>
			</div>
			{userType === "SuperAdmin" && (
				<SocialPostForm
					id={id}
					updatePosts={updatePosts}
					firebaseId={firebaseId}
				/>
			)}
			<div className="px-4 pb-4 flex flex-col gap-2 flex-1 relative">
				{loading ? (
					<SocialFeedListSkeleton />
				) : posts.length > 0 ? (
					posts.map((p) => (
						<SocialFeedItem
							key={p.id}
							userId={id}
							id={p.id}
							username={p.userName}
							timestamp={p.createdAt}
							content={p.content}
							likes={p.likesCount}
							comments={p.commentsCount}
							shares={p.sharesCount}
							isOwner={p.userId === id}
							isImportant={p.isImportant}
							images={p.imageUrls}
							onCommentClick={handleCommentClick}
							profilePic={p.profilePic}
							isLikedByUser={p.isLikedByUser}
							updatePosts={setPosts}
						/>
					))
				) : (
					<div className="p-10 flex justify-center items-center">
						<div className="text-sm font-semibold text-gray-400">No Data</div>
					</div>
				)}
				{totalPages > 1 && (
					<div className="pt-8">
						<div className="flex justify-center items-center mt-4 absolute bottom-4 left-0 right-0">
							<Pagination
								color="light"
								renderItem={(item) => (
									<PaginationItem
										components={{
											next: (props) => (
												<button {...props}>
													<BiRightArrow color="var(--color-icon)" />
												</button>
											),
											previous: (props) => (
												<button {...props}>
													<BiLeftArrow color="var(--color-icon)" />
												</button>
											),
										}}
										{...item}
										sx={{ color: "white" }}
									/>
								)}
								count={totalPages}
								page={page}
								onChange={(e, v) => setPage(v)}
							/>
						</div>
					</div>
				)}
			</div>

			{selectedPost && isCommentModalOpen && (
				<CommentModal
					userId={id}
					onClose={() => setIsCommentModalOpen(false)}
					post={{
						...selectedPost,
						username: selectedPost.userName,
						profilePic: selectedPost.profilePic,
						images: selectedPost.imageUrls,
					}}
					updatePosts={setPosts}
				/>
			)}
		</Card>
	);
};

export default SocialFeed;
